const PATTERN = /^\s*shell output for target/i;

function getTextContent(node: any): string {
  if (node.type === 'text') return node.value || '';
  if (!node.children) return '';
  return (node.children as any[]).map(getTextContent).join('');
}

function isBlockHeading(node: any): boolean {
  return node.type === 'element' && /^h[1-6]$/.test(node.tagName);
}

/**
 * Pull the executed command lines out of a console block. Command lines start
 * with `$ `; the trailing approval tag (e.g. ` [YES]`) is dropped, and the
 * shell's echo of the same command (no `$` prefix) is ignored.
 */
function extractCommands(text: string): string[] {
  return text
    .split('\n')
    .filter((l) => /^\$\s+/.test(l))
    .map((l) =>
      l
        .replace(/^\$\s+/, '')
        .replace(/\s*\[[A-Za-z]+\]\s*$/, '')
        .trim()
    )
    .filter(Boolean);
}

/**
 * Restyle the executor's `### Shell Output for target …` section: a
 * "## Terminal Output" heading, the list of commands that ran, then a collapsed
 * "Raw Terminal Data" <details> holding the full console block, reusing the
 * risk-dropdown styling.
 *
 * Display-only — it transforms the rendered HAST, never the message content —
 * so the raw markdown the LLM, resume memory, and report extraction read is
 * unchanged.
 */
export const collapsibleTerminalOutputPlugin = () => (tree: any) => {
  const children: any[] = tree.children || [];
  const out: any[] = [];
  let i = 0;

  while (i < children.length) {
    const node = children[i];

    if (
      node.type === 'element' &&
      node.tagName === 'h3' &&
      PATTERN.test(getTextContent(node))
    ) {
      const body: any[] = [node];
      i++;
      while (i < children.length && !isBlockHeading(children[i])) {
        body.push(children[i]);
        i++;
      }

      out.push({
        type: 'element',
        tagName: 'h2',
        properties: {},
        children: [{ type: 'text', value: 'Terminal Output' }]
      });

      const pre = body.find((n) => n.type === 'element' && n.tagName === 'pre');
      const commands = pre ? extractCommands(getTextContent(pre)) : [];
      if (commands.length) {
        out.push({
          type: 'element',
          tagName: 'div',
          properties: { className: ['cr-terminal-commands'] },
          children: commands.map((cmd) => ({
            type: 'element',
            tagName: 'div',
            properties: { className: ['cr-terminal-cmd'] },
            children: [{ type: 'text', value: cmd }]
          }))
        });
      }

      out.push({
        type: 'element',
        tagName: 'details',
        properties: {
          className: ['cr-risk-details', 'cr-terminal-output-details']
        },
        children: [
          {
            type: 'element',
            tagName: 'summary',
            properties: { className: ['cr-risk-summary'] },
            children: [{ type: 'text', value: 'Raw Terminal Data' }]
          },
          ...body
        ]
      });
    } else {
      out.push(node);
      i++;
    }
  }

  tree.children = out;
};
